import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { Controller } from 'react-hook-form'
import { toast } from 'sonner'

import useAffectedStaff from '../../../../../lib/hooks/use-affected-staff'
import useMultipleSchema from '../../../../../lib/hooks/use-multiple-schema'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { HobbyAudienceEnum } from '../../../../../lib/types/enums/hobby/hobby-audience.enum'
import { AffectedStaffQuestionnaireInput } from '../../../../../lib/types/models/affected-staff-questionnaire/affected-staff-questionnaire-input'
import { canAffectedStaffAccessQuestionnaire } from '../../../../../lib/utils/functions/affected-staff-questionnaire.functions'
import { Form, FormControl, FormField } from '../../../../elements/forms/form'
import ControlledInput from '../../../../elements/inputs/controlled-input'
import FamilyImagesUploader from '../../../client-space/child-profile/family-images-uploader'
import HobbiesSelection from '../../../hobby/hobbies-section-selection'
import {
  saveForLaterAffectedStaffQuestionnaireSchema,
  submitAffectedStaffQuestionnaireSchema
} from '../../utils/affected-staff-questionnaire-schema'
import StaffPersonalSpaceQuestionnaireDriverLicense from './staff-personal-space-questionnaire-driver-license'
import StaffPersonalSpaceQuestionnaireHealthSection from './staff-personal-space-questionnaire-health-section'
import StaffPersonalSpaceQuestionnairePersonalitySection from './staff-personal-space-questionnaire-personality-section'

type Props = {
  defaultValues?: AffectedStaffQuestionnaireInput
  handleSubmit: (data: AffectedStaffQuestionnaireInput) => Promise<void>
  handleSave: (data: AffectedStaffQuestionnaireInput) => Promise<void>
}

const StaffPersonalSpaceQuestionnaireForm = ({
  defaultValues,
  handleSubmit,
  handleSave
}: Props) => {
  const { t } = useTranslate()
  const { affectedStaff } = useAffectedStaff()
  const hasHostFamilyQuestionnaire = canAffectedStaffAccessQuestionnaire(affectedStaff)

  const { form, loading, submitForm } = useMultipleSchema<
    ['save-for-later', 'submit'],
    AffectedStaffQuestionnaireInput
  >(
    [
      { name: 'save-for-later', schema: saveForLaterAffectedStaffQuestionnaireSchema },
      {
        name: 'submit',
        schema: submitAffectedStaffQuestionnaireSchema(hasHostFamilyQuestionnaire, t)
      }
    ],
    async (values, name) => {
      if (name === 'submit') await handleSubmit(values)
      else await handleSave(values)
    },
    defaultValues
  )

  const validateFamilyImages = (mode: 'submit' | 'save-for-later') => {
    const images = form.watch('familyImages') ?? []
    if (images.some((img) => !img.path || !img.caption)) {
      toast.error(t('questionnaire.toast.error.familyImages'))
      return false
    }
    if ((!images.length || images.length < 3) && mode === 'submit' && hasHostFamilyQuestionnaire) {
      toast.error(t('questionnaire.toast.error.familyImagesMinimum'))
      return false
    }
    return true
  }

  return (
    <Form {...form}>
      <form>
        <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
          <div className="flex flex-col gap-3">
            <StaffPersonalSpaceQuestionnairePersonalitySection />
            <Controller
              control={form.control}
              name="hobbies"
              render={({ field }) => (
                <HobbiesSelection
                  value={field.value || []}
                  onChange={field.onChange}
                  audience={HobbyAudienceEnum.STAFF}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-3">
            <StaffPersonalSpaceQuestionnaireHealthSection />
            <StaffPersonalSpaceQuestionnaireDriverLicense />
          </div>
        </div>
        <div className="h-3.5" />
        {hasHostFamilyQuestionnaire && (
          <>
            <InnerBox
              title={t('affected.staff.questionnaire.host.family.letter')}
              className="p-4 rounded-lg"
            >
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('affected.staff.questionnaire.host.family.letter.note')}
              </Typography>
              <div className="h-2" />
              <FormField
                control={form.control}
                name={'hostFamilyLetter'}
                render={({ field }) => (
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="textarea"
                      rows={20}
                      max={600}
                      placeholder={t(
                        'affected.staff.questionnaire.host.family.letter.placeholder'
                      )}
                    />
                  </FormControl>
                )}
              />
            </InnerBox>
            <div className="h-3.5" />
            <Controller
              control={form.control}
              name="familyImages"
              render={({ field }) => (
                <FamilyImagesUploader
                  mode="secondary"
                  images={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
          </>
        )}
        <div className="h-3.5" />
        <InnerBox
          title={t('affected.staff.questionnaire.medical.release')}
          className="p-4 rounded-lg"
        >
          <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
            {`${t('affected.staff.questionnaire.medical.release.note.1')} ${toFullName(affectedStaff?.staff.firstName, affectedStaff?.staff.lastName)}, ${t('affected.staff.questionnaire.medical.release.note.2')}`}
          </Typography>
          <div className="h-3" />
          <Checkbox
            hasContainer
            hasError={!!form.formState.errors.hasMedicalAuthorization}
            component={
              <Typography variant="label-medium" color={tailwindColors.primary.DEFAULT}>
                {t('affected.staff.questionnaire.medical.release.checkbox.label')}
              </Typography>
            }
            checked={form.watch('hasMedicalAuthorization') === true}
            onChange={(value) =>
              form.setValue('hasMedicalAuthorization', value, { shouldValidate: true })
            }
          />
        </InnerBox>

        <div className="flex justify-end mt-4 gap-2.5 mobile:flex-col mobile:gap-2">
          <Button
            label={t('staff.personal.space.save.for.later.btn')}
            variant="ghost"
            type="button"
            loading={loading['save-for-later']}
            onClick={() => {
              if (!validateFamilyImages('save-for-later')) return
              submitForm('save-for-later')
            }}
          />
          <Button
            label={t('staff.personal.space.submit.btn')}
            variant="primary"
            type="button"
            loading={loading.submit}
            onClick={() => {
              if (!validateFamilyImages('submit')) return
              submitForm('submit')
            }}
          />
        </div>
      </form>
    </Form>
  )
}

export default StaffPersonalSpaceQuestionnaireForm
